import { useState, FormEvent, useEffect, useRef } from 'react';
import { db } from '../db/db';
import type { BusinessNature } from '../db/db';
import { useStore } from '../store/useStore';
import { seedCompany } from '../db/seed';
import { DateInput } from '../components/DateInput';
import { useEnterAsTab } from '../hooks/useEnterAsTab';
import { INDIAN_STATES } from '../constants/states';
import { AccountingEngine } from '../services/accountingEngine';
import { BUSINESS_NATURES } from '../config/businessNature';
import { AppModal } from '../components/AppModal';

const getCurrentFinancialYearStart = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return `${year}-04-01`;
};

const isFYExpired = (fyStart: string): boolean => {
  if (!fyStart) return false;
  const start = new Date(fyStart);
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  end.setDate(end.getDate() - 1);
  return new Date() > end;
};

// Get max companies from license — uses PLAN_LIMITS for consistent limits
const getMaxCompanies = (): number => {
  const licenseInfo = useStore.getState().licenseInfo;
  if (!licenseInfo) return 1; // no license = demo limit
  // Firestore maxCompanies field takes priority if set
  if (licenseInfo.maxCompanies && licenseInfo.maxCompanies > 0) return licenseInfo.maxCompanies;
  // Plan-based limits (same as PLAN_LIMITS in license.ts)
  if (licenseInfo.type === 'demo') return 1;
  if (licenseInfo.plan === 'unlimited') return 999;
  if (licenseInfo.plan === 'pro') return 10;
  if (licenseInfo.plan === 'basic') return 3;
  return 1; // free/unknown = 1
};

export const CompanyCreationView = ({ handleItemSelect, companyId }: any) => {
  const formRef = useRef<HTMLFormElement>(null);
  const setActiveCompany = useStore((state) => state.setActiveCompany);
  const activeCompany = useStore((state) => state.activeCompany);
  const [hasCompanies, setHasCompanies] = useState(true);
  const [fyResetStatus, setFyResetStatus] = useState<'idle' | 'confirm' | 'done' | 'error'>('idle');
  const [fyExpired, setFyExpired] = useState(false);
  const [modal, setModal] = useState<{ open: boolean; message: string; type?: 'info' | 'warning' | 'error' }>({ open: false, message: '' });

  const [formData, setFormData] = useState({
    name: '',
    financialYearStart: getCurrentFinancialYearStart(),
    booksBeginFrom: getCurrentFinancialYearStart(),
    address: '',
    gstin: '',
    state: '',
    mobile: '',
    email: '',
    pincode: '',
    businessNature: 'general' as BusinessNature,
  });

  useEnterAsTab(formRef);
  const isEdit = !!companyId;

  useEffect(() => {
    db.companies.count().then(count => setHasCompanies(count > 0));
    if (isEdit) {
      db.companies.get(companyId).then(company => {
        if (company) {
          setFormData({
            name: company.name,
            financialYearStart: company.financialYearStart,
            booksBeginFrom: company.booksBeginFrom,
            address: company.address || '',
            gstin: company.gstin || '',
            state: company.state || '',
            mobile: (company as any).mobile || '',
            email: (company as any).email || '',
            pincode: (company as any).pincode || '',
            businessNature: (company as any).businessNature || 'general',
          });
          setFyExpired(isFYExpired(company.financialYearStart));
        }
      });
    }
  }, [companyId, isEdit]);

  const showAlert = (message: string, type: 'info' | 'warning' | 'error' = 'warning') => {
    setModal({ open: true, message, type });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;

    // GSTIN validation
    if (formData.gstin) {
      const gstinRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}Z[A-Z\d]{1}$/;
      if (!gstinRegex.test(formData.gstin.trim().toUpperCase())) {
        showAlert('Invalid GSTIN format. Expected format: 27AAAAA0000A1Z5 (15 characters)', 'error');
        return;
      }
    }

    if (isEdit) {
      await db.companies.update(companyId, {
        name: formData.name,
        financialYearStart: formData.financialYearStart,
        booksBeginFrom: formData.booksBeginFrom,
        address: formData.address,
        gstin: formData.gstin,
        state: formData.state,
        mobile: formData.mobile,
        email: formData.email,
        pincode: formData.pincode,
        businessNature: formData.businessNature,
      });
      if (activeCompany?.id === companyId) {
        const updated = await db.companies.get(companyId);
        if (updated) setActiveCompany(updated);
      }
    } else {
      // Company limit check
      const maxCompanies = getMaxCompanies();
      const count = await db.companies.count();
      if (count >= maxCompanies) {
        // Fire the global trial-limit event so App.tsx shows the upgrade modal
        window.dispatchEvent(new CustomEvent('aarthika:trial-limit', {
          detail: {
            reason: `Trial limit reached: You can only create ${maxCompanies} company in demo mode. Upgrade to a full license for unlimited companies.`
          }
        }));
        return;
      }

      // Duplicate name check
      const exists = await db.companies.where('name').equals(String(formData.name).trim()).first();
      if (exists) {
        showAlert('A company with this name already exists.', 'warning');
        return;
      }

      const newId = await db.companies.add({
        name: formData.name,
        financialYearStart: formData.financialYearStart,
        booksBeginFrom: formData.booksBeginFrom,
        address: formData.address,
        gstin: formData.gstin,
        state: formData.state,
        mobile: formData.mobile,
        email: formData.email,
        pincode: formData.pincode,
        businessNature: formData.businessNature,
        isActive: 1
      });

      await seedCompany(newId);
      await db.companies.where('isActive').equals(1).modify({ isActive: 0 });
      await db.companies.update(newId, { isActive: 1 });
      const activatedCompany = await db.companies.get(newId);
      if (activatedCompany) setActiveCompany(activatedCompany);
    }

    handleItemSelect({ name: 'Quit' });
  };

  return (
    <div
      className="flex flex-col w-full h-full bg-[#f8faf7] z-50 absolute inset-0 font-sans text-[13px] text-black border-2 border-[#1e8585]"
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); if (hasCompanies) handleItemSelect({ name: 'Quit' }); }
      }}
    >
      {/* App-themed Modal */}
      <AppModal
        open={modal.open}
        type={modal.type || 'warning'}
        message={modal.message}
        onConfirm={() => setModal({ open: false, message: '' })}
      />

      <div className="bg-[#1e8585] text-white flex justify-between px-2 py-[2px] font-bold shadow-sm">
        <span>{isEdit ? 'Company Alteration' : 'Company Creation'}</span>
      </div>

      {isEdit && fyExpired && fyResetStatus !== 'done' && (
        <div className="bg-amber-50 border-b-2 border-amber-400 px-4 py-2 flex items-center gap-3 text-[13px]">
          <span className="text-amber-700 font-bold text-[16px]">⚠</span>
          <span className="text-amber-800 font-semibold flex-1">
            Current Financial Year has ended. Start a new year by updating the <strong>Financial Year beginning from</strong> date and resetting voucher numbers below.
          </span>
          <button
            type="button"
            onClick={() => {
              const current = new Date(formData.financialYearStart);
              current.setFullYear(current.getFullYear() + 1);
              const next = current.toISOString().split('T')[0];
              setFormData(f => ({ ...f, financialYearStart: next, booksBeginFrom: next }));
              setFyExpired(false);
            }}
            className="bg-amber-600 text-white px-4 py-1 font-bold hover:bg-amber-700 whitespace-nowrap"
          >
            Advance to Next FY
          </button>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} className="p-8 flex flex-col gap-4 max-w-[700px]">
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Company Name:</label>
          <input type="text" className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} autoFocus required />
        </div>
        <div className="flex items-start">
          <label className="w-[200px] font-bold pt-2">Business Nature:</label>
          <div className="flex-1">
            <div className="grid grid-cols-5 gap-2">
              {BUSINESS_NATURES.map(bn => {
                const isDisabled = bn.status !== 'active';
                const isSelected = formData.businessNature === bn.value;
                return (
                  <button key={bn.value} type="button" disabled={isDisabled}
                    onClick={() => !isDisabled && setFormData({ ...formData, businessNature: bn.value })}
                    className={`border rounded-lg px-2 py-2 text-center transition-colors relative ${
                      isSelected ? 'border-[#1e8585] bg-[#f0f9f9] ring-1 ring-[#1e8585]'
                      : isDisabled ? 'border-gray-200 bg-gray-50 opacity-40 cursor-not-allowed'
                      : 'border-gray-300 hover:bg-gray-50 cursor-pointer'
                    }`}>
                    <div className="text-[14px]">{bn.icon}</div>
                    <div className="text-[10px] font-semibold text-gray-700 mt-0.5 leading-tight">{bn.label}</div>
                    {isDisabled && (
                      <span className="absolute -top-1.5 -right-1.5 text-[7px] font-bold bg-gray-400 text-white px-1.5 py-[1px] rounded-full leading-none">SOON</span>
                    )}
                  </button>
                );
              })}
            </div>
            {formData.businessNature !== 'general' && (
              <div className="mt-2 text-[11px] text-[#1e8585] font-medium bg-[#f0f9f9] border border-[#cce8e8] rounded px-3 py-1.5">
                {BUSINESS_NATURES.find(b => b.value === formData.businessNature)?.description}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">State:</label>
          <select className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.state ?? ''} onChange={(e) => setFormData({ ...formData, state: e.target.value })}>
            <option value="">-- Select State --</option>
            {INDIAN_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">GSTIN:</label>
          <input type="text" className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.gstin} onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })} placeholder="27AAAAA0000A1Z5" />
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Financial Year beginning from:</label>
          <DateInput className="flex-1 bg-white" value={formData.financialYearStart} onChange={(val) => setFormData({ ...formData, financialYearStart: val })} />
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Books beginning from:</label>
          <DateInput className="flex-1 bg-white" value={formData.booksBeginFrom} onChange={(val) => setFormData({ ...formData, booksBeginFrom: val })} />
        </div>
        <div className="flex items-start">
          <label className="w-[200px] font-bold pt-1">Address:</label>
          <textarea className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white h-24 resize-none" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Pincode:</label>
          <input className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} placeholder="e.g. 380001" maxLength={6} />
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Mobile:</label>
          <input className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.mobile} onChange={(e) => setFormData({ ...formData, mobile: e.target.value })} placeholder="e.g. 9876543210" maxLength={10} />
        </div>
        <div className="flex items-center">
          <label className="w-[200px] font-bold">Email:</label>
          <input className="flex-1 border border-gray-400 px-2 py-1 outline-none focus:border-[#1e8585] bg-white" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="e.g. company@example.com" />
        </div>

        <div className="mt-8 flex gap-4 items-center">
          <button data-save-btn type="submit" className="bg-[#1e8585] text-white px-8 py-1 font-bold shadow-sm hover:bg-[#15666b]">Accept (End)</button>
          {hasCompanies && (<button data-quit-btn type="button" onClick={() => handleItemSelect({ name: 'Quit' })} className="bg-gray-200 px-8 py-1 font-bold shadow-sm hover:bg-gray-300">Cancel (Esc)</button>)}
          <span className="text-[11px] italic text-gray-400">Ctrl+A / End to save</span>
        </div>

        {isEdit && (
          <div className="mt-6 border-t border-gray-300 pt-4">
            <div className="flex items-center gap-4">
              <span className="w-[200px] font-bold text-[#b45309]">New Financial Year:</span>
              {fyResetStatus === 'idle' && (<button type="button" onClick={() => setFyResetStatus('confirm')} className="bg-[#b45309] text-white px-6 py-1 font-bold shadow-sm hover:bg-[#92400e]">Reset Voucher Numbers for New FY</button>)}
              {fyResetStatus === 'confirm' && (
                <div className="flex items-center gap-3">
                  <span className="text-[#b45309] font-semibold">This will reset all auto-numbered voucher series. Proceed?</span>
                  <button type="button" onClick={async () => { try { await AccountingEngine.resetVoucherNumbersForNewFY(companyId); setFyResetStatus('done'); } catch { setFyResetStatus('error'); } }} className="bg-red-600 text-white px-4 py-1 font-bold hover:bg-red-700">Yes, Reset</button>
                  <button type="button" onClick={() => setFyResetStatus('idle')} className="bg-gray-200 px-4 py-1 font-bold hover:bg-gray-300">Cancel</button>
                </div>
              )}
              {fyResetStatus === 'done' && (<span className="text-green-700 font-bold">✓ Voucher numbers reset successfully.</span>)}
              {fyResetStatus === 'error' && (<span className="text-red-600 font-bold">✗ Reset failed. Please try again.</span>)}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};